import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, QueryStatus, ScaledInput, useToast } from "@cx/ui";
import type { CommissionPreview, SellerInput, SellerView, TenantMemberView } from "@cx/contracts";
import { formatPct, toPct4, type CommissionBase } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ProductSearch } from "../prices/ProductSearch";
import { CommissionRulesBox } from "./CommissionRules";
import { COMMISSION_BASE_LABEL, commissionSourceText } from "./labels";

const pctNumber = (pct4: number) => formatPct(pct4).replace("%", "");
const settingsOf = (s: SellerView): SellerInput => ({
  defaultCommissionPct4: s.defaultCommissionPct4,
  commissionBase: s.commissionBase,
  canAddUnregisteredItems: s.canAddUnregisteredItems,
  canViewStock: s.canViewStock,
  canEditCreditLimit: s.canEditCreditLimit,
  status: s.status,
});

/** Vendedor: comissão e permissões, usuário de acesso, regras de comissão e simulação (RF-CAD-05). */
export function SellerPage(): ReactElement {
  const { id } = useParams({ from: "/cadastros/vendedores/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const seller = useQuery({
    queryKey: ["sellers", companyId, id],
    refetchOnMount: "always",
    queryFn: () => api<SellerView>(`/sellers/${id}`),
  });
  const [deleting, setDeleting] = useState(false);
  const del = useMutation({
    mutationFn: () => api(`/sellers/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Vendedor excluído");
      qc.invalidateQueries({ queryKey: ["sellers", companyId] });
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
      navigate({ to: "/cadastros/vendedores" });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const s = seller.data;
  return (
    <>
      <ContentHeader
        title={s?.name ?? "Vendedor"}
        breadcrumb={["Cadastros", "Vendedores", s?.name ?? ""]}
        actions={
          s && (
            <>
              <Link to="/cadastros/vendedores" className="btn btn-outline-secondary">
                Voltar
              </Link>
              {can(perms, "contacts", "read") && (
                <Link to="/cadastros/contatos/$id" params={{ id: s.id }} className="btn btn-outline-primary">
                  Dados do contato
                </Link>
              )}
              {can(perms, "sellers", "delete") && (
                <button type="button" className="btn btn-outline-danger" onClick={() => setDeleting(true)}>
                  Excluir vendedor
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={seller.isLoading} error={seller.error} />
      {s && (
        <>
          <SettingsBox key={JSON.stringify(settingsOf(s))} seller={s} />
          {can(perms, "users", "read") && <AccessBox seller={s} />}
          <CommissionRulesBox sellerId={s.id} />
          <SimulateBox sellerId={s.id} />
        </>
      )}
      <ConfirmDialog
        open={deleting}
        title="Excluir vendedor"
        message={`Excluir o vendedor "${s?.name ?? ""}"? O contato continua cadastrado, sem o tipo Vendedor. O vendedor pode ser restaurado pela Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          del.mutate();
          setDeleting(false);
        }}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}

function SettingsBox({ seller }: { seller: SellerView }) {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const canEdit = can(me.permissions as Permissions, "sellers", "update");
  const [v, setV] = useState<SellerInput>(settingsOf(seller));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof SellerInput>(k: K, value: SellerInput[K]) =>
    setV((x) => ({ ...x, [k]: value }));
  const save = useMutation({
    mutationFn: () => api(`/sellers/${seller.id}`, { method: "PUT", body: JSON.stringify(v) }),
    onSuccess: () => {
      toast("success", "Vendedor salvo");
      setErrors({});
      qc.invalidateQueries({ queryKey: ["sellers", companyId] });
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  const flags = [
    ["canAddUnregisteredItems", "Incluir produto não cadastrado nos pedidos"],
    ["canViewStock", "Ver estoque"],
    ["canEditCreditLimit", "Editar limite de crédito dos clientes"],
  ] as const;
  return (
    <div className="box card p-3 mb-3">
      <h2 className="h6">Comissão e permissões</h2>
      <fieldset disabled={!canEdit}>
        <div className="row">
          <Field label="Comissão padrão" className="col-md-4" error={errors.defaultCommissionPct4}>
            <ScaledInput
              value={v.defaultCommissionPct4}
              parse={toPct4}
              format={pctNumber}
              suffix="%"
              onChange={(p) => set("defaultCommissionPct4", p ?? 0)}
            />
          </Field>
          <Field
            label="Base da comissão"
            className="col-md-4"
            hint="Faturado: libera ao faturar. Recebido: proporcional a cada recebimento."
          >
            <select
              className="form-select"
              value={v.commissionBase}
              onChange={(e) => set("commissionBase", e.target.value as CommissionBase)}
            >
              <option value="invoiced">{COMMISSION_BASE_LABEL.invoiced}</option>
              <option value="received">{COMMISSION_BASE_LABEL.received}</option>
            </select>
          </Field>
          <Field label="Situação do vendedor" className="col-md-4">
            <select
              className="form-select"
              value={v.status}
              onChange={(e) => set("status", e.target.value as SellerInput["status"])}
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </Field>
        </div>
        <p className="form-label mb-1">
          Permissões do vendedor (valem quando ele entra com o próprio usuário)
        </p>
        {flags.map(([k, label]) => (
          <div key={k} className="form-check">
            <input
              id={`seller-${k}`}
              type="checkbox"
              className="form-check-input"
              checked={v[k]}
              onChange={(e) => set(k, e.target.checked)}
            />
            <label className="form-check-label" htmlFor={`seller-${k}`}>
              {label}
            </label>
          </div>
        ))}
        {canEdit && (
          <button
            type="button"
            className="btn btn-primary mt-3"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar vendedor
          </button>
        )}
      </fieldset>
    </div>
  );
}

/** Usuário de acesso (usuário-vendedor): quem entra com ele vê só a própria carteira de clientes. */
function AccessBox({ seller }: { seller: SellerView }) {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const canLink = can(perms, "sellers", "update") && can(perms, "users", "update");
  const members = useQuery({
    queryKey: ["seller-users", companyId],
    queryFn: () => api<{ data: TenantMemberView[] }>("/sellers/users"),
  });
  const [userId, setUserId] = useState(seller.user?.id ?? "");
  const link = useMutation({
    mutationFn: () =>
      api(`/sellers/${seller.id}/user`, { method: "PUT", body: JSON.stringify({ userId: userId || null }) }),
    onSuccess: () => {
      toast("success", userId ? "Usuário vinculado" : "Usuário desvinculado");
      qc.invalidateQueries({ queryKey: ["sellers", companyId] });
      qc.invalidateQueries({ queryKey: ["seller-users", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const options = (members.data?.data ?? []).filter(
    (m) => m.userType !== "accountant" && m.userType !== "api" && (!m.sellerId || m.sellerId === seller.id),
  );
  return (
    <div className="box card p-3 mb-3">
      <h2 className="h6">Usuário de acesso</h2>
      <p className="form-text mt-0">
        O usuário vinculado passa a ver só os clientes deste vendedor, cadastra clientes na carteira dele e
        segue as permissões acima.
      </p>
      <QueryStatus loading={members.isLoading} error={members.error} />
      <div className="row align-items-end">
        <Field label="Usuário" className="col-md-6">
          <select
            className="form-select"
            value={userId}
            disabled={!canLink}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">Nenhum (sem acesso próprio)</option>
            {options.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.email})
              </option>
            ))}
          </select>
        </Field>
        {canLink && (
          <div className="col-md-6 mb-3">
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={link.isPending || userId === (seller.user?.id ?? "")}
              onClick={() => link.mutate()}
            >
              Salvar acesso
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Simulação da comissão (05 §6) para um produto e um desconto concedido. */
function SimulateBox({ sellerId }: { sellerId: string }) {
  const { companyId, api } = useSession();
  const [product, setProduct] = useState<{ id: string; name: string } | null>(null);
  const [discount, setDiscount] = useState<number | null>(0);
  const r = useQuery({
    queryKey: ["sellers", companyId, sellerId, "commission", product?.id, discount],
    enabled: !!product && discount !== null,
    queryFn: () =>
      api<CommissionPreview>(
        `/sellers/${sellerId}/commission?${new URLSearchParams({
          productId: product!.id,
          discountPct4: String(discount),
        })}`,
      ),
  });
  return (
    <div className="box card p-3">
      <h2 className="h6">Simular comissão</h2>
      <ProductSearch
        id="commission-product-search"
        label="Produto"
        action="Simular"
        onPick={(p) => setProduct({ id: p.id, name: p.name })}
      />
      <div className="row">
        <Field label="Desconto concedido" className="col-md-4">
          <ScaledInput value={discount} parse={toPct4} format={pctNumber} suffix="%" onChange={setDiscount} />
        </Field>
      </div>
      {product && (
        <div aria-live="polite" data-testid="simulated-commission">
          <QueryStatus loading={r.isLoading} error={r.error} />
          {r.data && (
            <p className="mb-0">
              <strong>{product.name}</strong>: comissão de {formatPct(r.data.pct4)}{" "}
              <span className="text-muted">({commissionSourceText(r.data)})</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
